import { Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';

import type { MarketplaceProperty } from '../types';

const historyLabels = {
  COMING_SOON: 'Coming soon',
  FOR_SALE: 'Listed for sale',
  OFF_MARKET: 'Taken off market',
  SALE_PENDING: 'Sale pending',
  SOLD: 'Sold',
  UNKNOWN: 'Listing updated',
  WITHDRAWN: 'Listing withdrawn',
} satisfies Record<MarketplaceProperty['listingHistory'][number]['state'], string>;

export const PdpPriceHistory = ({
  listingHistory,
}: Pick<MarketplaceProperty, 'listingHistory'>) => {
  const events = listingHistory.flatMap((event) => {
    if (!event.timestamp || (event.state === 'SALE_PENDING' && !event.mls)) return [];
    const date = new Date(event.timestamp);
    if (!Number.isFinite(date.getTime())) return [];
    return [{ ...event, date }];
  });

  return (
    <Col as="section" aria-label="Price history" className="gap-10">
      <Text as="h2" className="text-label-lg">
        Price History
      </Text>
      {events.length === 0 ? (
        <Text className="text-body-md text-od-content-secondary">No price history</Text>
      ) : (
        /* Rows are separated by a 1px #EAE8E0 rule with 16px above and below. */
        <Col role="list" className="divide-y divide-[#EAE8E0]">
          {events.map((event, index) => {
            const offMarket = event.state === 'OFF_MARKET' || event.state === 'WITHDRAWN';
            const priceCents =
              event.state === 'SOLD'
                ? event.soldPriceCents
                : (event.currentListPriceCents ?? event.originalListPriceCents);
            const hasPrice = !offMarket && priceCents != null;
            const statusLabel = historyLabels[event.state];
            return (
              <Row
                role="listitem"
                key={`${event.timestamp}-${event.state}-${index}`}
                className="flex-wrap items-center gap-x-4 gap-y-2 py-4 first:pt-0 last:pb-0 sm:gap-x-8"
              >
                <time
                  dateTime={event.date.toISOString()}
                  aria-label={event.date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    timeZone: 'UTC',
                  })}
                  className="flex w-[104px] shrink-0 items-center gap-2 text-od-content-primary"
                >
                  <Text
                    as="span"
                    className={cn(
                      'flex h-7 min-w-[52px] items-center justify-center rounded-full px-2 text-[14px] leading-none',
                      index === 0
                        ? 'bg-[#CCE2FF] text-blue-950'
                        : 'bg-[#EAE8E0] text-od-content-secondary',
                    )}
                  >
                    {event.date.getUTCFullYear()}
                  </Text>
                  <Text as="span" className="whitespace-nowrap text-body-xs leading-none">
                    {event.date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </Text>
                </time>
                <Text className="shrink-0 text-[18px] leading-[1.4] text-[#231E1B]">
                  {hasPrice ? formatCurrency(priceCents / 100) : statusLabel}
                </Text>
                {(hasPrice || event.mls) && (
                  <Text className="basis-full whitespace-pre-wrap text-body-sm text-[#514C48] sm:flex-1 sm:basis-0 sm:text-right">
                    {hasPrice
                      ? `${statusLabel}${event.mls ? `   •   ${event.mls}` : ''}`
                      : event.mls}
                  </Text>
                )}
              </Row>
            );
          })}
        </Col>
      )}
    </Col>
  );
};
